<template>
  <div class="paused-player-slot" :class="[`team-${team.toLowerCase()}`, statusClass]">
    <!-- Player Content -->
    <div class="pps-content">
      <div class="pps-avatar">
        <img
          v-if="player.profile_picture"
          :src="getProfilePicturePath(player.profile_picture)"
          :alt="displayName"
        />
        <div v-else class="pps-initials">{{ initials }}</div>
        <!-- Status Badge -->
        <div class="pps-status-badge" :class="player.status">
          <span class="material-symbols-outlined">{{ statusIcon }}</span>
        </div>
      </div>
      <div class="pps-info">
        <span class="pps-name">
          {{ displayName }}
          <span v-if="player.is_bot" class="pps-bot">Bot</span>
        </span>
        <span class="pps-status-text">{{ statusText }}</span>
        <span class="pps-seat">Seat {{ player.seat_position + 1 }}</span>
      </div>
      <!-- Actions (Creator Only) -->
      <div v-if="isCreator && canReplaceWithBot" class="pps-actions">
        <button
          class="pps-action-btn replace-bot"
          @click="$emit('replace-with-bot', player.seat_position)"
          title="Replace with bot"
        >
          <span class="material-symbols-outlined">smart_toy</span>
          Add Bot
        </button>
      </div>
      <div v-if="isCreator && canSendInvite" class="pps-actions">
        <button
          class="pps-action-btn send-invite"
          @click="handleSendInvite"
          title="Send invite"
        >
          <span class="material-symbols-outlined">send</span>
          Send Invite
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface PausedPlayer {
  seat_position: number
  team: string
  username: string
  display_name: string
  profile_picture: string
  is_bot: boolean
  current_id: string
  original_user_id?: string | null
  replaced_by_bot?: boolean
  status: 'rejoined' | 'waiting' | 'bot'
}

const props = defineProps<{
  player: PausedPlayer
  team: 'A' | 'B'
  isCreator: boolean
}>()

const emit = defineEmits<{
  (e: 'replace-with-bot', seatPosition: number): void
  (e: 'send-invite', seatPosition: number): void
}>()

const displayName = computed(() => {
  if (props.player.is_bot) return props.player.username
  return props.player.display_name || props.player.username || 'Player'
})

const initials = computed(() => {
  const name = displayName.value
  const parts = name.split(' ')
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
})

const statusClass = computed(() => {
  return `status-${props.player.status}`
})

const statusIcon = computed(() => {
  switch (props.player.status) {
    case 'rejoined':
      return 'check_circle'
    case 'waiting':
      return 'hourglass_empty'
    case 'bot':
      return 'smart_toy'
    default:
      return 'help'
  }
})

const statusText = computed(() => {
  switch (props.player.status) {
    case 'rejoined':
      return 'Ready'
    case 'waiting':
      return 'Waiting to rejoin...'
    case 'bot':
      return 'AI Player'
    default:
      return ''
  }
})

const canReplaceWithBot = computed(() => {
  // Can only replace humans who haven't rejoined
  return props.player.status === 'waiting' && !props.player.is_bot
})

const canSendInvite = computed(() => {
  // Can send invite to humans who haven't rejoined
  return props.player.status === 'waiting' && !props.player.is_bot
})

function getProfilePicturePath(picture: string): string {
  if (!picture) return ''
  // Handle both formats: "1" and "profiles/1.png"
  if (picture.startsWith('profiles/')) {
    return `/images/${picture}`
  }
  return `/images/profiles/${picture}.png`
}

function handleSendInvite(): void {
  // For now, just copy the room code. In the future, could integrate with share API.
  emit('send-invite', props.player.seat_position)
}
</script>

<style scoped>
.paused-player-slot {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(12px);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 20px 24px;
  min-width: 0;
  min-height: 90px;
  transition: all 0.3s ease;
}

.paused-player-slot.team-a {
  border-left: 4px solid var(--team-1-color, #c9a227);
}

.paused-player-slot.team-b {
  border-left: 4px solid var(--team-2-color, #c94a4a);
}

/* Status-based styling */
.paused-player-slot.status-rejoined {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.05) 100%);
  border-color: rgba(34, 197, 94, 0.3);
}

.paused-player-slot.status-waiting {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(251, 191, 36, 0.03) 100%);
  border-color: rgba(251, 191, 36, 0.25);
  border-style: dashed;
}

.paused-player-slot.status-bot {
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(147, 51, 234, 0.03) 100%);
  border-color: rgba(147, 51, 234, 0.25);
}

.paused-player-slot:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

/* Content layout */
.pps-content {
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
}

.pps-avatar {
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  overflow: visible;
  flex-shrink: 0;
}

.pps-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  border: 3px solid rgba(201, 162, 39, 0.4);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
}

.pps-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(201, 162, 39, 0.3), rgba(201, 162, 39, 0.1));
  color: var(--accent-primary, #c9a227);
  font-weight: 700;
  font-size: 20px;
  border-radius: 50%;
  border: 3px solid rgba(201, 162, 39, 0.4);
}

/* Status badge on avatar */
.pps-status-badge {
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--bg-primary, #0d1f2d);
}

.pps-status-badge.rejoined {
  background: #22c55e;
  color: white;
}

.pps-status-badge.waiting {
  background: #fbbf24;
  color: #1a2f3f;
  animation: pulse 2s infinite;
}

.pps-status-badge.bot {
  background: #9333ea;
  color: white;
}

.pps-status-badge .material-symbols-outlined {
  font-size: 14px;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

/* Info section */
.pps-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.pps-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 6px;
}

.pps-bot {
  font-size: 11px;
  padding: 3px 8px;
  background: rgba(147, 51, 234, 0.2);
  color: #a855f7;
  border-radius: 4px;
  font-weight: 600;
}

.pps-status-text {
  font-size: 13px;
  font-weight: 500;
}

.status-rejoined .pps-status-text {
  color: #22c55e;
}

.status-waiting .pps-status-text {
  color: #fbbf24;
}

.status-bot .pps-status-text {
  color: #a855f7;
}

.pps-seat {
  font-size: 12px;
  color: var(--text-muted, #8899a6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Action buttons */
.pps-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pps-action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  border: 1px solid transparent;
}

.pps-action-btn .material-symbols-outlined {
  font-size: 18px;
}

.pps-action-btn.replace-bot {
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.2) 0%, rgba(147, 51, 234, 0.1) 100%);
  border-color: rgba(147, 51, 234, 0.3);
  color: #a855f7;
}

.pps-action-btn.replace-bot:hover {
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.3) 0%, rgba(147, 51, 234, 0.15) 100%);
  border-color: rgba(147, 51, 234, 0.5);
  transform: translateY(-2px);
}

.pps-action-btn.send-invite {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%);
  border-color: rgba(59, 130, 246, 0.3);
  color: #60a5fa;
}

.pps-action-btn.send-invite:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(59, 130, 246, 0.15) 100%);
  border-color: rgba(59, 130, 246, 0.5);
  transform: translateY(-2px);
}

/* Material icons */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}
</style>
